'use client';

import {
  useCallback,
  useState,
  createContext,
  useContext,
  useEffect,
  useMemo,
  ReactNode,
} from 'react';
import type {
  ReviewAndExportProviderProps,
  ExportableDocument,
} from './ReviewAndExportProvider.types';
import { UserContext } from '@/_contexts/UserProvider';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import type { Company } from '@/_types/models/company';
import {
  getEntitiesWithTaxForms,
  getSupportedTaxFormsForDataEntry,
  getWorkerStatus,
} from '@/_api/admin-review-and-export';
import { useDebounce } from '@/_hooks/useDebounce';
import type { PaginationState } from '@tanstack/react-table';
import type { WorkerStatus } from '@/_types/workerServers';
import { getAllExportableDocumentsForEntity } from '@/_api/admin-companies';
import { ReviewAndExportFilterObject } from '@/_types/reviewAndExport';
import { DEFAULT_REVIEW_AND_EXPORT_FILTER } from '@/_constants/review-and-export';
import {
  REVIEW_AND_EXPORT_PAGE_SIZE_COOKIE,
  REVIEW_AND_EXPORT_FILTERS_COOKIE,
} from '@/_constants/cookies';
import {
  setCookie,
  getCookie,
  deleteCookie,
  type CookieValueTypes,
} from 'cookies-next/client';
import { areSameObjects } from '@/_utils/objects';
import { EntityTagsDialog } from '@/_contexts/admin/EntityV2Provider/_components/EntityTagsDialog';

const DEFAULT_PAGINATION_STATE: PaginationState = {
  pageIndex: 0,
  pageSize: 10,
};

const DEBOUNCE_TIME = 480 as const;

const ReviewAndExportContext = createContext<ReviewAndExportProviderProps>({
  descending: false,
  filter: DEFAULT_REVIEW_AND_EXPORT_FILTER,
  filteredEntitiesWithTaxForms: [],
  getEntitiesWithTaxForms: () => Promise.resolve(),
  getSupportedTaxForms: () => {},
  getWorkerStatus: () => Promise.resolve(),
  hasDrake: false,
  hasLacerte: false,
  hasUltraTax: false,
  isLoading: false,
  isFiltering: false,
  mismatchErrors: {},
  nameMismatch: {},
  nonExportableCounts: {},
  nonExportableFetchError: false,
  onFilterChange: () => null,
  pagination: DEFAULT_PAGINATION_STATE,
  resetFilters: () => null,
  reviewId: null,
  rowsPerPage: 10,
  searchTerm: '',
  searchTermDebounced: '',
  setDescending: () => {},
  setMismatchErrorForDocument: (_: number, __: boolean) => {},
  setNameMismatchForDocument: (_: number, __: boolean) => {},
  setPagination: () => {},
  setReviewId: () => {},
  setRowsPerPage: () => {},
  setSearchTerm: () => {},
  setSortBy: () => {},
  setYear: () => {},
  sortBy: 'name',
  supportedTaxForms: [],
  totalCount: 0,
  updateEntityWithTaxForms: () => {},
  workerStatus: 'loading',
  year: 2024,
  processingCounts: {},
  setProcessingCountForEntity: () => {},
  selectedEntitiesWithTaxForms: [],
  setSelectedEntitiesWithTaxForms: () => {},
  validateEntityForScheduling: () => ({ valid: true }),
  toggleEntityTagsDialog: () => null,
  showEntityTags: false,
});

const ReviewAndExportProvider = ({ children }: { children: ReactNode }) => {
  const [processingCounts, setProcessingCounts] = useState<
    Record<number, number>
  >({});
  const [mismatchErrors, setMismatchErrors] = useState<Record<number, boolean>>(
    {},
  );
  const [nameMismatch, setNameMismatch] = useState<Record<number, boolean>>({});
  const { isAdvisor, token } = useContext(UserContext);
  const { firm, preferences } = useContext(FirmSettingsContext);

  const [entitiesWithTaxForms, setEntitiesWithTaxForms] = useState<Array<any>>(
    [],
  );
  const [year, setYear] = useState<number>(2024);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [sortBy, setSortBy] = useState<string>('name');
  const [descending, setDescending] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [totalCount, setTotalCount] = useState<number>(0);
  const [reviewId, setReviewId] = useState<null | number>(null);
  const [supportedTaxForms, setSupportedTaxForms] = useState<string[]>([]);
  const [isLoading, setLoading] = useState<boolean>(true);
  const [pagination, setPagination] = useState<PaginationState>(
    DEFAULT_PAGINATION_STATE,
  );
  const [filter, setFilter] = useState<ReviewAndExportFilterObject>(
    DEFAULT_REVIEW_AND_EXPORT_FILTER,
  );
  const [workerStatus, setWorkerStatus] = useState<WorkerStatus>('loading');
  const [nonExportableCounts, setNonExportableCounts] = useState<
    Record<string, number>
  >({});
  const [nonExportableFetchError, setNonExportableFetchError] =
    useState<boolean>(false);
  const [selectedEntities, setSelectedEntities] = useState<Array<any>>([]);
  const [showEntityTags, setShowEntityTags] = useState<boolean>(false);

  const isFiltering: boolean = useMemo(() => {
    return !areSameObjects(filter, DEFAULT_REVIEW_AND_EXPORT_FILTER);
  }, [filter]);

  const resetFilters = (): void => {
    setFilter(DEFAULT_REVIEW_AND_EXPORT_FILTER);
    deleteCookie(REVIEW_AND_EXPORT_FILTERS_COOKIE);
  };

  const toggleEntityTagsDialog = () => setShowEntityTags(!showEntityTags);

  // Enhanced onFilterChange function
  const onFilterChange = (newFilter: Partial<ReviewAndExportFilterObject>) => {
    const newFilters: ReviewAndExportFilterObject = {
      ...filter,
      ...newFilter,
    };
    setFilter(newFilters);
    setPagination((previous) => ({ ...previous, pageIndex: 0 }));
  };

  const hasUltraTax: boolean = !!firm?.tax_softwares?.includes('UltraTax');
  const hasDrake: boolean = !!firm?.tax_softwares?.includes('Drake');
  const hasLacerte: boolean = !!firm?.tax_softwares?.includes('Lacerte');

  const searchTermDebounced = useDebounce(searchTerm, DEBOUNCE_TIME);

  // Cookie management for filters
  useEffect(() => {
    const readFilterCookies = () => {
      try {
        const filtersCookie: CookieValueTypes = getCookie(
          REVIEW_AND_EXPORT_FILTERS_COOKIE,
        );

        if (filtersCookie) {
          const parsed: ReviewAndExportFilterObject = JSON.parse(filtersCookie);
          if (parsed && typeof parsed === 'object') {
            setFilter(parsed);
          }
        }
      } catch (error) {
        console.error('Error reading filter cookies:', error);
      }
    };

    readFilterCookies();
  }, []);

  useEffect(() => {
    const updateFilterCookies = () => {
      try {
        const filtersCookie = JSON.stringify(filter);
        setCookie(REVIEW_AND_EXPORT_FILTERS_COOKIE, filtersCookie);
      } catch (error) {
        console.error('Error updating filter cookies:', error);
      }
    };

    if (filter && !areSameObjects(filter, DEFAULT_REVIEW_AND_EXPORT_FILTER)) {
      updateFilterCookies();
    }
  }, [filter]);

  // Search term handling that resets pagination
  useEffect(() => {
    if (!!searchTermDebounced) {
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }
  }, [searchTermDebounced]);

  // Page size cookie management
  useEffect(() => {
    const pageSize = pagination?.pageSize;
    if (pageSize && pageSize !== DEFAULT_PAGINATION_STATE.pageSize) {
      setCookie(REVIEW_AND_EXPORT_PAGE_SIZE_COOKIE, pageSize.toString());
    }
  }, [pagination?.pageSize]);

  useEffect(() => {
    const pageSizeCookie = getCookie(REVIEW_AND_EXPORT_PAGE_SIZE_COOKIE);

    if (pageSizeCookie) {
      const parsed: number = parseInt(pageSizeCookie as string);

      if (parsed && !isNaN(parsed)) {
        setPagination((prev) => ({
          ...prev,
          pageSize: parsed,
        }));
      }
    }
  }, []);

  const _getEntitiesWithTaxForms = useCallback(
    async (isSilent?: boolean) => {
      if (!token || !isAdvisor || !preferences?.review_and_export_enabled) {
        return;
      }

      try {
        if (!isSilent && !!searchTermDebounced) setLoading(true);

        const params = {
          year,
          page: pagination.pageIndex + 1,
          rows_per_page: rowsPerPage,
          sort_by: sortBy,
          descending,
          search_term: searchTermDebounced,
          ...filter, // Spread all filter properties
        };

        const { count: totalCount } = await getEntitiesWithTaxForms({
          token,
          ...{
            ...params,
            count: true,
          },
        });

        const entitiesWithTaxForms = await getEntitiesWithTaxForms({
          token,
          ...params,
        });

        setTotalCount(totalCount);
        Array.isArray(entitiesWithTaxForms) &&
          setEntitiesWithTaxForms(entitiesWithTaxForms);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    },
    [
      descending,
      isAdvisor,
      pagination,
      rowsPerPage,
      searchTermDebounced,
      sortBy,
      token,
      year,
      filter,
      preferences?.review_and_export_enabled,
    ],
  );

  const currentTaxSoftware = useMemo((): string | null => {
    return firm?.tax_softwares?.[0] || null;
  }, [firm?.tax_softwares]);

  const updateEntityWithTaxForms = (entity: Company) => {
    if (!entity?.id) return;

    const existingEntityIndex = entitiesWithTaxForms.findIndex(
      (u) => u.id === entity.id,
    );
    const newEntitiesWithTaxForms = [...entitiesWithTaxForms];
    newEntitiesWithTaxForms[existingEntityIndex] = {
      ...newEntitiesWithTaxForms[existingEntityIndex],
      ...entity,
    };
    setEntitiesWithTaxForms(newEntitiesWithTaxForms);
  };

  const _getSupportedTaxForms = useCallback(async () => {
    if (!token || !isAdvisor || !preferences?.review_and_export_enabled) {
      return;
    }

    if (!currentTaxSoftware) return;

    try {
      const supportedTaxForms = await getSupportedTaxFormsForDataEntry({
        token,
        taxSoftware: currentTaxSoftware,
      });

      setSupportedTaxForms(supportedTaxForms);
    } catch (error) {
      console.error(error);
    }
  }, [
    isAdvisor,
    token,
    currentTaxSoftware,
    preferences?.review_and_export_enabled,
  ]);

  const _getWorkerStatus = useCallback(async () => {
    if (!token || !isAdvisor || !preferences?.review_and_export_enabled) {
      return;
    }

    try {
      const { status, error } = await getWorkerStatus({
        token,
      });

      const workerStatus = !!error ? 'error' : status;
      setWorkerStatus(workerStatus);
    } catch (error) {
      console.error(error);
      setWorkerStatus('error');
    }
  }, [isAdvisor, token, preferences?.review_and_export_enabled]);

  // Backend now handles assigned_advisor_id filtering, so no client-side filtering needed
  const filteredEntitiesWithTaxForms = entitiesWithTaxForms;

  const setMismatchErrorForDocument = useCallback(
    (documentId: number, value: boolean) => {
      setMismatchErrors((prev) => ({ ...prev, [documentId]: value }));
    },
    [],
  );

  const setNameMismatchForDocument = useCallback(
    (documentId: number, value: boolean) => {
      setNameMismatch((prev) => ({ ...prev, [documentId]: value }));
    },
    [],
  );

  const _fetchCountsForPage = useCallback(async () => {
    if (!token || !isAdvisor || !preferences?.review_and_export_enabled) {
      return;
    }

    const counts: Record<number, number> = {};
    const processingCountsByEntity: Record<number, number> = {};
    const newMismatchErrors: Record<number, boolean> = {};
    const newNameMismatch: Record<number, boolean> = {};

    let encounteredError = false;

    const uncachedEntities = filteredEntitiesWithTaxForms.filter(
      (entity) => nonExportableCounts[entity.id] === undefined,
    );

    if (uncachedEntities.length === 0) return;

    await Promise.all(
      uncachedEntities.map(async (entity) => {
        try {
          const response = await getAllExportableDocumentsForEntity({
            id: entity.id,
            year,
            token,
          });
          const docs = (
            'data' in response ? response.data : response
          ) as Array<ExportableDocument>;
          const nonExportable = docs.filter((doc) => {
            return !(
              doc &&
              supportedTaxForms.includes(doc.name) &&
              doc.extracted_document_fields?.length > 0 &&
              doc.status !== 'not_exportable'
            );
          });
          const stuckInProcessing = docs.filter(
            (doc) =>
              ![
                'action_required',
                'review_required',
                'analysis_succeeded',
                'not_exportable',
                'scheduled',
                'reviewed',
              ].includes(doc.status) && supportedTaxForms.includes(doc.name),
          );
          counts[entity.id] = nonExportable.length;
          setProcessingCountForEntity(entity.id, stuckInProcessing.length);

          // Populate validation state from API response
          docs.forEach((doc) => {
            if (doc.has_taxpayer_spouse_mismatch) {
              newMismatchErrors[doc.id] = true;
            }
            if (doc.has_name_mismatch) {
              newNameMismatch[doc.id] = true;
            }
          });
        } catch (error) {
          encounteredError = true;
          counts[entity.id] = 0;
        }
      }),
    );

    setNonExportableCounts((prev) => ({ ...prev, ...counts }));
    setNonExportableFetchError(encounteredError);
    setProcessingCounts((prev) => ({ ...prev, ...processingCountsByEntity }));
    setMismatchErrors((prev) => ({ ...prev, ...newMismatchErrors }));
    setNameMismatch((prev) => ({ ...prev, ...newNameMismatch }));
  }, [
    isAdvisor,
    filteredEntitiesWithTaxForms,
    nonExportableCounts,
    supportedTaxForms,
    token,
    preferences?.review_and_export_enabled,
    year,
  ]);

  const setProcessingCountForEntity = (companyId: number, count: number) => {
    setProcessingCounts((prev) => ({
      ...prev,
      [companyId]: count,
    }));
  };

  const selectedEntitiesWithTaxForms = selectedEntities;
  const setSelectedEntitiesWithTaxForms = setSelectedEntities;

  const validateEntityForScheduling = ({
    entity,
    docs,
    entitySSN,
    spouseSSN,
  }: {
    entity: any;
    docs: any[];
    entitySSN?: string;
    spouseSSN?: string;
  }): { valid: boolean; error?: string } => {
    if (hasDrake || hasUltraTax) {
      if (!entitySSN) {
        return { valid: false, error: 'Please enter a valid SSN/TIN.' };
      }

      const cleanSSN = entitySSN.replace(/-/g, '');
      if (cleanSSN.length !== 9 || isNaN(Number(cleanSSN))) {
        return { valid: false, error: 'Please enter a valid SSN/TIN.' };
      }

      if (hasUltraTax) {
        const requiresSpouse = docs.some((doc) => {
          const field = doc.extracted_document_fields.find(
            (f: any) => f.key === 'taxpayer_or_spouse',
          );
          const value = field?.corrected_value || field?.value;
          return value === 'spouse';
        });

        if (requiresSpouse) {
          const cleanSpouseSSN = spouseSSN?.replace(/-/g, '');
          if (!spouseSSN) {
            return { valid: false, error: 'Spouse SSN/TIN is required.' };
          }
          if (cleanSpouseSSN?.length !== 9 || isNaN(Number(cleanSpouseSSN))) {
            return {
              valid: false,
              error: 'Please enter a valid spouse SSN/TIN.',
            };
          }
        }
      }
    }

    if (hasLacerte) {
      const id = entity.tax_return_id;
      if (!id || id.length > 8) {
        return { valid: false, error: 'Please enter a valid tax return ID.' };
      }
    }

    return { valid: true };
  };

  // Fetch entities with tax forms when dependencies change
  useEffect(() => {
    _getEntitiesWithTaxForms();
  }, [_getEntitiesWithTaxForms]);

  // Fetch supported tax forms when dependencies change
  useEffect(() => {
    _getSupportedTaxForms();
  }, [_getSupportedTaxForms]);

  // Fetch worker status when dependencies change
  useEffect(() => {
    _getWorkerStatus();
  }, [_getWorkerStatus]);

  // Fetch counts for entities when dependencies change
  useEffect(() => {
    _fetchCountsForPage();
  }, [_fetchCountsForPage]);

  return (
    <ReviewAndExportContext
      value={{
        descending,
        filter,
        filteredEntitiesWithTaxForms,
        getEntitiesWithTaxForms: _getEntitiesWithTaxForms,
        getSupportedTaxForms: _getSupportedTaxForms,
        getWorkerStatus: _getWorkerStatus,
        hasDrake,
        hasLacerte,
        hasUltraTax,
        isLoading,
        isFiltering,
        mismatchErrors,
        nameMismatch,
        nonExportableCounts,
        nonExportableFetchError,
        onFilterChange,
        pagination,
        resetFilters,
        reviewId,
        rowsPerPage,
        searchTerm,
        searchTermDebounced,
        setDescending,
        setMismatchErrorForDocument,
        setNameMismatchForDocument,
        setPagination,
        setReviewId,
        setRowsPerPage,
        setSearchTerm,
        setSortBy,
        setYear,
        sortBy,
        supportedTaxForms,
        totalCount,
        updateEntityWithTaxForms,
        workerStatus,
        year,
        processingCounts,
        setProcessingCountForEntity,
        selectedEntitiesWithTaxForms,
        setSelectedEntitiesWithTaxForms,
        validateEntityForScheduling,
        toggleEntityTagsDialog,
        showEntityTags,
      }}
    >
      {children}
      {showEntityTags && (
        <EntityTagsDialog
          isOpen={showEntityTags}
          onClose={toggleEntityTagsDialog}
        />
      )}
    </ReviewAndExportContext>
  );
};

export { ReviewAndExportContext, ReviewAndExportProvider };
